import React, { useState, useEffect } from "react";
import { Card, CardBody, Button, Row, Col, Spinner } from "reactstrap";
import empty_illustration from "assets/img/images/empty.svg";
import RejectVerificationModal from "modals/RejectVerificationModal";
import helpers from "utils/helpers";
import userServices from "services/userServices";
import "assets/css/custom.css";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import { useAuth } from "context/AuthContext";
import PhoneIcon from "@mui/icons-material/Phone";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const UserInfo = ({ localUserData, country, pNum, countryPhoneCode }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { user, permission } = useAuth();
  const canUser = permission?.permissions?.modules?.users || {};
  const [status, setStatus] = useState("pending");
  const [adminRemarks, setAdminRemarks] = useState([]);
  const [rejectModal, setRejectModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const rejectionOptions = helpers.getRejectionOptions(t);
  const isDeleted = localUserData?.isDeleted;

  useEffect(() => {
    if (localUserData?.verificationStatus) {
      setStatus(localUserData.verificationStatus);
      const storedRemarks = localUserData?.adminRemarks;
      setAdminRemarks(Array.isArray(storedRemarks) ? storedRemarks : []);
    }
  }, [localUserData]);

  const toggleRejectModal = () => setRejectModal(!rejectModal);

  const handleStatusChange = (newStatus) => {
    if (isDeleted || loadingAction) return;
    if (newStatus === "approved") {
      statusVerification("approved", []);
    } else {
      toggleRejectModal();
    }
  };

  const confirmRejection = async () => {
    if (!adminRemarks || adminRemarks.length === 0) {
      notify("error", t("page.users.details.enterRemarks", "Please enter a reason or remarks."));
      return;
    }
    await statusVerification("rejected", adminRemarks);
    toggleRejectModal();
  };

  const statusVerification = async (newStatus, remarks) => {
    try {
      setLoadingAction(newStatus);
      const res = await userServices.userVerification(localUserData._id, {
        status: newStatus,
        remarks,
        adminId: user?._id,
      });
      setStatus(newStatus);
      setAdminRemarks([]);
      const payload = res?.data || res;
      notify("success", t(payload?.messageKey || "page.users.details.statusUpdated", payload?.message || t("page.users.details.statusUpdated", "Status updated successfully")));
    } catch (error) {
      console.error("Verification Status Change Error:", error);
      const errPayload = error?.response?.data || {};
      notify("error", t(errPayload?.messageKey || errPayload?.error || "page.users.details.updateFailed", errPayload?.message || t("page.users.details.updateFailed", "Failed to update status")));
    } finally {
      setLoadingAction(null);
    }
  };

  const hasData =
    pNum || (country && country.label) || localUserData?.uploadedIdNumber;

  const statusColorMap = {
    approved: { border: "#6ED06C", text: "success" },
    rejected: { border: "#FF725E", text: "danger" },
    pending: { border: "#FFB020", text: "warning" },
  };
  const currentStatusStyle = statusColorMap[status] || statusColorMap.pending;

  const riskScore = localUserData?.riskScore || 0;
  const riskLevel = localUserData?.riskLevel || (riskScore >= 80 ? "critical" : (riskScore >= 50 ? "warning" : "low"));
  
  const riskLevelColorMap = {
    critical: { border: "#f5365c", text: "danger" },
    warning: { border: "#fb6340", text: "warning" },
    low: { border: "#2dce89", text: "success" },
  };
  const riskLevelColor = riskLevelColorMap[riskLevel] || riskLevelColorMap.low;

  const isAdminRole =
    user?.role?.slug === "admin" || user?.role?.slug === "super_admin";
  const diditConsoleUrl = isAdminRole
    ? helpers.getDiditConsoleUrl(localUserData?.diditSessionId)
    : null;

  return (
    <div className="user-info-modern mt-4">
      <Card className="shadow border-0">
        <CardBody className="p-4">
          <div className="verification-console-wrapper">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start mb-2">
              <div className="mb-3 mb-md-0">
                <h2 className="console-title">
                  {t("page.users.details.userDetails", "User Details")}
                </h2>
                <p className="console-subtitle mb-0">
                  {t("page.users.details.verificationDesc", "Review applicant identity credentials.")}
                </p>
              </div>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: "12px" }}>
                {/* Verification Status Card */}
                <Card
                  className="shadow-sm border-0 overflow-hidden status-indicator-card mb-0"
                  style={{ borderLeftColor: currentStatusStyle.border }}
                >
                  <CardBody className="py-2 px-3 d-flex align-items-center">
                    <div>
                      <small className="field-label d-block text-muted text-xs font-weight-600">
                        {t("page.users.details.verificationStatus", "Verification Status")}
                      </small>
                      <span
                        className={`badge badge-${currentStatusStyle.text} text-uppercase`}
                        style={{ fontSize: "0.85rem", padding: "0.35em 0.65em" }}
                      >
                        {t(`page.user-profile.status.${status}`, status)}
                      </span>
                    </div>
                  </CardBody>
                </Card>

                {/* Risk Score Card */}
                <Card
                  className="shadow-sm border-0 overflow-hidden status-indicator-card mb-0"
                  style={{ borderLeftColor: riskLevelColor.border }}
                >
                  <CardBody className="py-2 px-3 d-flex align-items-center">
                    <div>
                      <small className="field-label d-block text-muted text-xs font-weight-600">
                        {t("page.user-profile.header.riskScore", "Risk Score")}
                      </small>
                      <span
                        className={`badge badge-${riskLevelColor.text} text-uppercase`}
                        style={{ fontSize: "0.85rem", padding: "0.35em 0.65em" }}
                      >
                        {riskScore} ({t(`categories.moderation.severity.${riskLevel}`, riskLevel).toUpperCase()})
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>

            {hasData ? (
              <Row className="gx-3">
                <Col lg="8" className="d-flex">
                  <Card className="shadow-sm border-0 verification-card w-100">
                    <CardBody className="px-4 pb-4 pt-0 pt-md-4 d-flex flex-column">
                      <h4 className="heading-md text-muted mb-2">
                        {t("page.users.details.basicInfo", "Basic Info")}
                      </h4>
                      <Row className="align-items-start">
                        <Col md="6" className="mb-4 mb-md-0">
                          <p className="heading-sm mb-0">
                            <strong>{t("page.user-profile.fullName", "Full Name")}</strong>
                          </p>
                          <p className="heading-sm mb-0">
                            {localUserData?.firstName} {localUserData?.lastName}
                          </p>
                        </Col>
                        <Col md="6" className="mb-4 mb-md-0">
                          <p className="heading-sm mb-0">
                            <strong>{t("page.users.details.contactInfo", "Contact Info")}</strong>
                          </p>
                          <div className="d-flex align-items-center">
                            <PhoneIcon className="text-primary mr-2" style={{ fontSize: "1.1rem" }} />
                            <p className="heading-sm mb-0">
                              +{countryPhoneCode?.phone} {pNum}
                            </p>
                          </div>
                        </Col>
                      </Row>
                      <Row className="align-items-start mt-md-3">
                        <Col md="6" className="mb-4 mb-md-0">
                          <p className="heading-sm mb-0">
                            <strong>{t("page.user-profile.country", "Country")}</strong>
                          </p>
                          <div className="d-flex align-items-center">
                            {country?.code && (
                              <img
                                src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                                width={24}
                                className="rounded-sm shadow-sm mr-2"
                                alt="flag"
                              />
                            )}
                            <p className="heading-sm mb-0">{country?.label}</p>
                          </div>
                        </Col>
                        <Col md="6">
                          <p className="heading-sm mb-0">
                            <strong>{t("page.users.details.addressInfo", "Address Info")}</strong>
                          </p>
                          <p className="heading-sm mb-0">
                            {localUserData?.homeAddress || t("common.empty", "Empty")}
                          </p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
                <Col lg="4" className="d-flex">
                  <Card
                    className="shadow-sm border-0 h-80 verification-card w-100"
                    style={{ borderTop: "4px solid #11cdef" }}
                  >
                    <CardBody className="px-4 pb-4 pt-0 pt-md-4 d-flex flex-column">
                      <h4 className="heading-md text-muted mb-2">
                        {t("page.users.details.uploadedIdInfo", "Uploaded ID Info")}
                      </h4>
                      <div className="mb-2">
                        <p className="heading-sm mb-0">
                          <strong>{t("page.users.details.uploadedIdType", "Uploaded ID Type")}</strong>
                        </p>
                        <p className="heading-sm mb-0">
                          {helpers.getUserIdTypeLabel(localUserData?.uploadedIdType, t)}
                        </p>
                      </div>
                      <div>
                        <p className="heading-sm mb-0">
                          <strong>{t("page.user-profile.form.idNumber", "ID Number")}</strong>
                        </p>
                        <p className="heading-sm mb-0">
                          {localUserData?.uploadedIdNumber}
                        </p>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            ) : (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="py-5 text-center">
                  <img src={empty_illustration} width={120} className="mb-3 opacity-5" alt="empty" />
                  <h5 className="text-muted font-weight-bold">
                    {t("page.users.details.noUserInfo", "No User Information Available")}
                  </h5>
                </CardBody>
              </Card>
            )}

            {/* Suspension Information Card (only if suspended) */}
            {(localUserData?.accountStatus === "suspended" || localUserData?.isSuspended) && (
              <Card className="shadow-sm border-0 mb-4">
                <CardBody className="p-4">
                  <h4 className="heading-md text-danger mb-3 d-flex align-items-center">
                    <i className="fa fa-ban mr-2" />
                    {t("page.user-profile.header.suspensionTitle", "Suspension Information")}
                  </h4>
                  <Row className="gy-3">
                    <Col md="3" sm="6" xs="12" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("page.user-profile.header.suspensionStatus", "Suspension Status")}
                      </span>
                      <span className="badge badge-danger text-uppercase font-weight-bold" style={{ fontSize: "11px", padding: "6px 12px" }}>
                        {t("page.user-profile.status.suspended", "Suspended")}
                      </span>
                    </Col>

                    <Col md="4" sm="6" xs="12" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("page.user-profile.header.suspensionReason", "Suspension Reason")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm" style={{ wordBreak: "break-word" }}>
                        {localUserData?.suspensionDetails?.reason || localUserData?.selectedReason || (Array.isArray(localUserData?.adminRemarks) && localUserData.adminRemarks[0]) || "Repeated policy violations"}
                      </span>
                    </Col>

                    <Col md="3" sm="6" xs="12" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("page.user-profile.header.suspendedOn", "Suspended On")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">
                        {(localUserData?.suspensionDetails?.startedAt || localUserData?.statusUpdatedAt)
                          ? helpers.getFormattedDate(localUserData?.suspensionDetails?.startedAt || localUserData?.statusUpdatedAt, t)
                          : "—"}
                      </span>
                    </Col>

                    <Col md="2" sm="6" xs="12">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        {t("page.user-profile.header.suspendedUntil", "Until")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">
                        {localUserData?.suspensionDetails?.expiresAt
                          ? helpers.getFormattedDate(localUserData.suspensionDetails.expiresAt, t)
                          : t("page.user-profile.header.permanent", "Permanent")}
                      </span>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            )}

            {status === "pending" && canUser?.update && (
              <Card className="shadow-sm border-0 verification-card mb-4 mt-4">
                <CardBody className="px-4 py-3 d-flex flex-column flex-md-row justify-content-between align-items-center">
                  <div className="mb-3 mb-md-0">
                    <h4 className="heading-md text-muted mb-1">
                      {t("page.users.details.pendingAction", "Pending Verification Action")}
                    </h4>
                    <small className="text-muted">
                      {t("page.users.details.actionDesc", "Please review and choose an action.")}
                    </small>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {diditConsoleUrl && (
                      <Button
                        color="info"
                        outline
                        className="px-3 d-flex align-items-center"
                        tag="a"
                        href={diditConsoleUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon style={{ fontSize: "1rem" }} className="mr-1" />
                        {localUserData?.diditVerificationSubmitted
                          ? t("page.users.details.viewDiditVerification", "View Didit Verification")
                          : t("page.users.details.viewDiditSession", "View Didit Session (In Progress)")}
                      </Button>
                    )}
                    <Button
                      color="success"
                      className="px-4"
                      disabled={isDeleted || !!loadingAction}
                      onClick={() => handleStatusChange("approved")}
                    >
                      {loadingAction === "approved" && <Spinner size="sm" className="mr-2" />}
                      {t("page.users.details.approveVerification", "Approve Document Verification")}
                    </Button>
                    <Button
                      color="danger"
                      className="px-4"
                      disabled={isDeleted || !!loadingAction}
                      onClick={() => handleStatusChange("rejected")}
                    >
                      {loadingAction === "rejected" && <Spinner size="sm" className="mr-2" />}
                      {t("page.users.details.denyVerification", "Deny Verification")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )}

            <RejectVerificationModal
              isOpen={rejectModal}
              toggle={toggleRejectModal}
              onConfirm={confirmRejection}
              rejectionOptions={rejectionOptions}
              adminRemarks={adminRemarks}
              setAdminRemarks={setAdminRemarks}
              helpers={helpers}
              t={t}
              isLoading={!!loadingAction}
              isDeleted={isDeleted}
            />
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default UserInfo;
